import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Page,
  TextField,
  Button,
  Stack,
} from "@towersystems/roam-common-native";
import { SaleAdjustmentType } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Colors, Spacings, Text, View } from "react-native-ui-lib";
import { CartItemRootParmList } from "../../../../types";
import {
  compareLessThanEqualsWithTolerance,
  compareLessThanWithTolerance,
} from "../../../../utilities/compare-with-tolerance";
import SaleItem from "../../../../utilities/data/model/SaleItem";
import {
  isValidPositiveMoneyValue,
  isValidPositiveWholeNumber,
} from "../../../../utilities/is-valid-money";
import { moneyToFormattedString } from "../../../../utilities/money";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  calculateDiscountAmount,
  DiscountType,
} from "../../utils/calculate-discounted-amount";
import { useActiveCartItem } from "../CartItemModalRootScreen/hooks";

interface DiscountCartItemModalScreenProps
  extends NativeStackScreenProps<
    CartItemRootParmList,
    "DiscountCartItemModalScreen"
  > {}

export const DiscountCartItemModalScreen = ({
  navigation,
}: DiscountCartItemModalScreenProps) => {
  const [newItemPrice, setNewItemPrice] = useState<number | undefined>(
    undefined
  );
  const { item } = useActiveCartItem();
  const [_, setInputError] = useState<string | undefined>();
  const shouldFocus = useHasTransitionEnded(navigation);
  const inputEl = useRef<any>(null);

  const [selectedDiscountType, setSelectedDiscountType] =
    useState<DiscountType>("amount");

  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    navigation.setOptions({
      title: `Maximum Discount: ${moneyToFormattedString(item?.price || 0)}`,
    });
  }, [item]);

  const canAddDiscount = useMemo(() => {
    const maxDiscount = item.price;
    switch (selectedDiscountType) {
      case "price":
        if (!isValidPositiveMoneyValue(inputValue)) {
          return false;
        }
        return compareLessThanWithTolerance(Number(inputValue), maxDiscount);
      case "amount":
        if (!isValidPositiveMoneyValue(inputValue)) {
          return false;
        }
        return compareLessThanEqualsWithTolerance(
          Number(inputValue),
          maxDiscount
        );

      case "percent":
        if (!isValidPositiveWholeNumber(inputValue)) {
          return false;
        }
        return compareLessThanEqualsWithTolerance(Number(inputValue), 100);
    }
  }, [item, selectedDiscountType, inputValue]);

  useEffect(() => {
    if (canAddDiscount) {
      const discountAmount = calculateDiscountAmount(
        item.price,
        selectedDiscountType,
        parseFloat(inputValue)
      );
      setNewItemPrice(item.price - discountAmount);
    }
  }, [item, selectedDiscountType, inputValue]);

  const handleOnAddDiscount = useCallback(
    async (item: SaleItem, amount: number) => {
      return item
        .addAdjustment({
          type: SaleAdjustmentType.MANUAL_DISCOUNT,
          amount,
        })
        .then(() => navigation.goBack())
        .catch((e) => console.log(e));
    },
    [item, navigation]
  );

  const handleOnSave = useCallback(async () => {
    if (canAddDiscount) {
      const discountAmount = calculateDiscountAmount(
        item.price,
        selectedDiscountType,
        parseFloat(inputValue)
      );
      return handleOnAddDiscount(item, discountAmount);
    }
  }, [
    handleOnAddDiscount,
    selectedDiscountType,
    canAddDiscount,
    item,
    inputValue,
  ]);

  const createHandleOnPress = (discountType: DiscountType) => () => {
    setSelectedDiscountType(discountType);
    setInputError(undefined);
    if (discountType === "price") {
      setInputValue(item.price.toString());
    } else {
      setInputValue("");
    }
    inputEl.current?.focus();
  };

  const valid = canAddDiscount || inputValue.length == 0;

  const formatterType =
    inputValue.length >= 1 && valid
      ? selectedDiscountType === "percent"
        ? "percentage"
        : "money"
      : undefined;

  const footerMarkup = (
    <Button
      backgroundColor={Colors.action}
      color="white"
      disabled={!canAddDiscount}
      label="Apply Discount"
      onPress={handleOnSave}
    />
  );

  return (
    <Page
      title="Discount Cart Item"
      secondaryTitle="Apply a discount to line item"
      footer={footerMarkup}
    >
      <View>
        <Text
          style={[
            {
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s2,
            },
          ]}
        >
          Discount Type
        </Text>
        <View flex row>
          <Stack fill>
            <View flex>
              <Button
                fullWidth
                label="Amount ($)"
                isAction
                outline={!(selectedDiscountType === "amount")}
                onPress={createHandleOnPress("amount")}
                style={[{ borderColor: "rgb(218, 219, 221)", borderWidth: 2 }]}
              ></Button>
            </View>
            <View flex>
              <Button
                fullWidth
                label="Percentage (%)"
                isAction
                outline={!(selectedDiscountType === "percent")}
                onPress={createHandleOnPress("percent")}
                style={[{ borderColor: "rgb(218, 219, 221)", borderWidth: 2 }]}
              ></Button>
            </View>
            <View flex>
              <Button
                fullWidth
                label="Price"
                isAction
                outline={!(selectedDiscountType === "price")}
                onPress={createHandleOnPress("price")}
                style={[{ borderColor: "rgb(218, 219, 221)", borderWidth: 2 }]}
              ></Button>
            </View>
          </Stack>
        </View>
      </View>
      <View marginT-40>
        <Text
          style={[
            {
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s2,
            },
          ]}
        >
          Discount Amount
        </Text>
        <TextField
          ref={inputEl}
          value={inputValue}
          placeholder="Amount"
          onChangeText={(v) => setInputValue(v)}
          autoFocus={shouldFocus}
          formatterType={formatterType}
          keyboardType="numeric"
          validationMessage={
            !valid ? `Enter a valid discount value` : undefined
          }
        />
      </View>
      {newItemPrice !== undefined && valid && (
        <View>
          <Text>{`New Item Price: ${moneyToFormattedString(
            newItemPrice
          )}`}</Text>
        </View>
      )}
    </Page>
  );
};
